import React, { useEffect, useMemo, useState } from "react";
import {
  ComponentSingleStyleConfig,
  useDisclosure,
  useOutsideClick,
  useStyleConfig,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { ValueObjectMetaEnum } from "../../../../lib";
import {
  ComponentStyleProps,
  componentStylePropsOf,
} from "../../../../lib/styles/props/component-style-props";
import { hasValue } from "../../../../lib/util/common-util";
import { CMButtonSearchCondition } from "../CMButtonSearchCondition/CMButtonSearchCondition";
import { CMButtonPopupSearchConditionCheckbox } from "../../button-popup";

export type CMButtonSearchConditionCheckboxProps<
  T extends [string, ...string[]],
> = {
  label: string;

  values?: T[number][] | null;

  hideClearButton?: boolean;

  /**
   * 値オブジェクトメタデータ
   */
  valueObjectMeta: ValueObjectMetaEnum<T>;

  labelSubmit?: string;
  labelClear?: string;

  onChange?: (value: T[number][] | null) => void;
} & ComponentStyleProps;

export const CMButtonSearchConditionCheckbox = <
  T extends [string, ...string[]],
>({
  label,

  values = null,

  hideClearButton = false,

  valueObjectMeta,

  labelSubmit,
  labelClear,

  onChange,

  sx,
  ...rest
}: CMButtonSearchConditionCheckboxProps<T>) => {
  const styles = useStyleConfig("CMButtonSearchConditionCheckboxStyles");

  const { t } = useTranslation();

  const {
    isOpen: isOpenPopup,
    onClose: onClosePopup,
    onToggle: onTogglePopup,
  } = useDisclosure();

  const displayValues = useMemo(() => {
    const _displayValues =
      values?.map((v) => t(valueObjectMeta.dict[v])).filter(hasValue) ?? [];
    if (_displayValues.length === 0) return undefined;
    return _displayValues;
  }, [t, valueObjectMeta, values]);

  const [internalValues, setInternalValues] = useState<T[number][]>(
    values ?? [],
  );
  useEffect(() => {
    setInternalValues(values ?? []);
  }, [values]);

  const resetAllInternalState = (newValues: T[number][] | null = values) => {
    setInternalValues(newValues ?? []);
  };

  const handleChangeInternalValues = (vs: T[number][]) => {
    setInternalValues(vs);
  };

  /**
   * Submitボタン押下時、呼び出し元に選択値を返却する。
   * （ただし空の場合は代わりにnullを返却する。）
   */
  const handleSubmit = () => {
    onClosePopup();

    if (internalValues.length <= 0) {
      resetAllInternalState(null);
      onChange?.(null);
    } else {
      // 次の処理で、正しい順序に並べ替える。
      const sortedValues = valueObjectMeta.values.filter((v) =>
        internalValues.includes(v),
      );
      resetAllInternalState(sortedValues);
      onChange?.(sortedValues);
    }
  };

  /**
   * Clearボタン押下時、内部値には空配列を設定し、呼び出し元にはnullを返却する。
   */
  const handleClear = () => {
    onClosePopup();
    resetAllInternalState();
    onChange?.(null);
  };

  const ref = React.useRef(null);
  useOutsideClick({
    ref: ref,
    handler: () => {
      onClosePopup();
      resetAllInternalState();
    },
  });

  return (
    <CMButtonSearchCondition
      label={label}
      displayValue={displayValues}
      isOpenPopup={isOpenPopup}
      onClick={onTogglePopup}
      sx={{ ...styles, ...sx }}
      {...rest}
      ref={ref}
    >
      <CMButtonPopupSearchConditionCheckbox<T>
        values={values}
        valueObjectMeta={valueObjectMeta}
        labelSubmit={labelSubmit}
        labelClear={labelClear}
        hideClearButton={hideClearButton}
        internalValues={internalValues}
        onChangeInternalValues={handleChangeInternalValues}
        onSubmit={handleSubmit}
        onClear={handleClear}
      />
    </CMButtonSearchCondition>
  );
};
export const CMButtonSearchConditionCheckboxStyles: ComponentSingleStyleConfig =
  {
    baseStyle: {},
  };
